import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../../assets/icons/close.svg";
import APIVariables from "../../../../services/api-variables";
import { handleCatch, validateEmail } from "../../../../shared/helpers";
import { App } from "../../../../routes/Constants";
import QuitModal from "../../../common/Modal/QuitModal";
import ConfirmNurseModal from "./ConfirmNurseModal";
import { ReactComponent as Undo } from "../../../../assets/icons/undo.svg";
import Axios from "../../../../services/Axios";

const inputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none disabled:cursor-not-allowed";

const requiredInputStyles = `${inputStyles} error`;

const NurseAdd = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nurseForm, setNurseForm] = useState({});
  const [quitModalIsOpen, setIsOpenQuit] = useState(false);
  const [confirmModalIsOpen, setConfirmModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hospitalDets, setHospitalDets] = useState({
    hospital_address: "",
    hospital_name: "",
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noChangesOnForm = Object.values(nurseForm).every(
    (item) => item.length === 0
  );

  const getData = async () => {
    await Axios.get(APIVariables.USERS_PROFILE)
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          setHospitalDets({
            hospital_address: data.hospital.address,
            hospital_name: data.hospital.value,
          });
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setNurseForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  function closeForm() {
    setIsOpenQuit(true);
  }

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.DASHBOARD);
  }

  const goToNursePage = () => {
    navigate(App.NURSES);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal(false);
    goToNursePage();
  };

  const nextPage = async () => {
    setIsSubmitting(false);
    setErrorMessage("");
    if (areAllFields) {
      await Axios.post(APIVariables.USERS_SIGNUP_CONNECTED_NURSES, nurseForm)
        .then((res) => {
          if (res?.status === 200) {
            setConfirmModal(true);
          }
        })
        .catch(function (error) {
          handleCatch(error);
          const errors = error?.response?.data;
          const errorsArray = [];
          errorsArray.push(errors);
          setErrorMessage(errorsArray);
        });
    } else {
      setIsSubmitting(true);
    }
  };

  const validateForm = ({
    first_name = "",
    last_name = "",
    job_title = "",
    email = "",
    registration_number = "",
  }) => ({
    first_name: {
      isValid: first_name.trim().length > 0,
    },
    last_name: {
      isValid: last_name.trim().length > 0,
    },
    job_title: {
      isValid: job_title.trim().length > 0,
    },
    registration_number: {
      isValid: registration_number.trim().length > 0,
    },
    email: {
      isValid: email.trim().length > 0,
    },
    emailValid: {
      isValid: validateEmail(email),
    },
  });

  const { first_name, last_name, job_title, email, registration_number } =
    nurseForm;

  const validateScreenFields = validateForm({
    first_name,
    last_name,
    job_title,
    email,
    registration_number,
  });

  let areAllFields = Object.values(validateScreenFields).every((field) => {
    return field.isValid;
  });

  const isEmailInvalid = errorMessage && errorMessage[0]?.email;

  return (
    <div
      className="min-h-screen overflow-y-auto pt-[140px] pb-[100px]"
      data-testid="nurses-add-page"
    >
      <div className="relative mx-auto max-w-[1100px] rounded-[10px] bg-white px-6 py-5 shadow-[0px_0px_40px_rgba(0,0,0,0.2)] sm:px-10">
        <button
          type="button"
          className="absolute right-[15px] top-[15px] cursor-pointer rounded-full bg-[#9ABDB8] p-[3px]"
          onClick={closeForm}
          aria-label="Close form"
          data-testid="nurses-add-close-button"
        >
          <img src={CloseIcon} alt="" className="brightness-0 invert" />
        </button>
        <div className="font-size-18 blue-label">Step 1/1</div>
        <h1 className="select-category-txt mineShaft-color mt-2.5 pb-1.5">
          Connected Nurse Registration Form
        </h1>
        <hr className="my-0 h-px border-0 bg-[#768692]" />

        <h2 className="title-details mineShaft-color mt-[30px] mb-[25px]">
          Nurse Details
        </h2>

        <div className="mb-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="nurse-first-name">
                Nurse First Name:*
              </label>
              <input
                id="nurse-first-name"
                type="text"
                onChange={handleChange}
                name="first_name"
                value={nurseForm.first_name || ""}
                maxLength={30}
                data-testid="nurses-add-first-name-input"
                className={
                  isSubmitting && !validateScreenFields.first_name.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
              />
              {isSubmitting && !validateScreenFields.first_name?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
            <div>
              <label className="blue-label" htmlFor="nurse-last-name">
                Nurse Last Name:*
              </label>
              <input
                id="nurse-last-name"
                type="text"
                onChange={handleChange}
                name="last_name"
                value={nurseForm.last_name || ""}
                maxLength={30}
                data-testid="nurses-add-last-name-input"
                className={
                  isSubmitting && !validateScreenFields.last_name.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
              />
              {isSubmitting && !validateScreenFields.last_name?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="nurse-registration">
                Registration number:*
              </label>
              <input
                id="nurse-registration"
                type="text"
                onChange={handleChange}
                name="registration_number"
                value={nurseForm.registration_number || ""}
                maxLength={30}
                data-testid="nurses-add-registration-input"
                className={
                  isSubmitting && !validateScreenFields.registration_number.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
              />
              {isSubmitting && !validateScreenFields.registration_number?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
            <div>
              <label className="blue-label" htmlFor="nurse-job-title">
                Job Title:*
              </label>
              <input
                id="nurse-job-title"
                type="text"
                onChange={handleChange}
                name="job_title"
                value={nurseForm.job_title || ""}
                maxLength={30}
                data-testid="nurses-add-job-title-input"
                className={
                  isSubmitting && !validateScreenFields.job_title.isValid
                    ? requiredInputStyles
                    : inputStyles
                }
              />
              {isSubmitting && !validateScreenFields.job_title?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="nurse-hospital-name">
                Hospital Name:
              </label>
              <input
                id="nurse-hospital-name"
                type="text"
                name="hospital_label"
                value={hospitalDets?.hospital_name || ""}
                disabled
                className={inputStyles}
                data-testid="nurses-add-hospital-name-input"
              />
            </div>
            <div>
              <label className="blue-label" htmlFor="nurse-email">
                Email Address: (A link will be sent to verify your email address)*
              </label>
              <input
                id="nurse-email"
                type="text"
                onChange={handleChange}
                name="email"
                value={nurseForm.email || ""}
                maxLength={50}
                data-testid="nurses-add-email-input"
                className={
                  (isSubmitting && !validateScreenFields.email.isValid) ||
                    (isSubmitting &&
                      !validateScreenFields.emailValid.isValid &&
                      validateScreenFields.email.isValid)
                    ? requiredInputStyles
                    : inputStyles
                }
              />
              {isSubmitting && !validateScreenFields.email?.isValid && (
                <span className="required-text">Required</span>
              )}
              {isSubmitting &&
                !validateScreenFields.emailValid.isValid &&
                validateScreenFields.email.isValid && (
                  <span className="required-text">Invalid Email</span>
                )}
              {isEmailInvalid && <span className="required-text">{isEmailInvalid}</span>}
            </div>
          </div>

          <div className="mt-4">
            <label className="blue-label" htmlFor="nurse-hospital-address">
              Hospital Address:
            </label>
            <input
              id="nurse-hospital-address"
              type="text"
              name="hospital_address"
              value={hospitalDets?.hospital_address || ""}
              disabled
              className={inputStyles}
              data-testid="nurses-add-hospital-address-input"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 pt-[60px] sm:flex-row sm:items-center sm:justify-between">
          <p
            className="text-base blue-label"
            data-testid="nurses-add-mandatory-field-note"
          >
            *symbol indicating a mandatory field
          </p>
          <div className="flex items-center justify-end gap-3">
            {!noChangesOnForm && (
              <button
                type="button"
                className="flex items-center gap-1 border-0 bg-transparent font-size-18 text-[#00567a] hover:text-[#8c8c8c]"
                onClick={() => setNurseForm({})}
                data-testid="nurses-add-undo-button"
              >
                <Undo /> Undo Changes
              </button>
            )}

            <button
              type="button"
              className="primary-button"
              onClick={nextPage}
              data-testid="nurses-add-save-button"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description="Are you sure you want to cancel filling out this form? All progress will be lost and you will need to start again."
        />
      )}
      {confirmModalIsOpen && (
        <ConfirmNurseModal
          closeModal={handleCloseConfirmModal}
          modalIsOpen={confirmModalIsOpen}
        />
      )}
    </div>
  );
};
export default NurseAdd;
